<template>
  <div class="klarna-return">
    <div class="klarna-return-content">
      <!-- Loading -->
      <div v-if="loading" class="klarna-loading">
        <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 100 100">
          <circle cx="50" cy="50" fill="none" stroke="#e9ff86" stroke-width="10" r="35" stroke-dasharray="165 57">
            <animateTransform attributeName="transform" type="rotate" repeatCount="indefinite" dur="0.5s" values="0 50 50;360 50 50" keyTimes="0;1" />
          </circle>
        </svg>
        <p class="klarna-status-text">Confirming your payment...</p>
      </div>

      <!-- Pending — Klarna approved but has not settled yet -->
      <div v-if="pending" class="klarna-pending">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#e9ff86" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
        <h2>Payment Being Confirmed</h2>
        <p>Klarna has approved your payment and it is still settling. Nothing else is needed from you. We'll email you as soon as your order is confirmed.</p>
      </div>

      <!-- Error -->
      <div v-if="error" class="klarna-error">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <h2>Payment Not Completed</h2>
        <p>{{ errorMessage }}</p>
        <button class="klarna-retry-btn" @click="goBackToCheckout">
          Return to Checkout <span>→</span>
        </button>
      </div>
    </div>

    <!-- Priority Physician Review — offered once the Klarna order is confirmed.
         Klarna leaves no card on file, so this path collects one. -->
    <fast-track-upsell-modal
      v-if="fastTrackOffer"
      :addon="fastTrackAddon"
      payment-method="klarna"
      :context="fastTrackOffer"
      @shown="onFastTrackShown"
      @purchased="onFastTrackPurchased"
      @done="onFastTrackDone"
    />
  </div>
</template>

<script>
import { loadStripe } from "@stripe/stripe-js";
import fastTrackUpsell from '@/mixins/fastTrackUpsell.js';
import { readFastTrackContext } from '@/utils/fastTrackUpsell.js';
import { firePendingKlarnaPurchase } from '@/utils/klarnaPurchaseTracking.js';
import { offerQuery } from '@/utils/freeNadLifeOffer.js';

export default {
  name: 'KlarnaReturn',
  mixins: [fastTrackUpsell],
  data() {
    return {
      loading: true,
      pending: false,
      error: false,
      errorMessage: '',
      baseUrl: process.env.VUE_APP_API_ENDPOINT,
    };
  },
  async mounted() {
    await this.confirmPayment();
  },
  methods: {
    async confirmPayment() {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const clientSecret = urlParams.get('payment_intent_client_secret');
        const paymentIntentId = urlParams.get('payment_intent');

        if (!clientSecret || !paymentIntentId) {
          this.error = true;
          this.errorMessage = 'Missing payment information. Please try again.';
          this.loading = false;
          return;
        }

        // Check PaymentIntent status via Stripe.js
        const stripe = await loadStripe(process.env.VUE_APP_STRIPE_KEY);
        const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);

        if (paymentIntent.status === 'succeeded') {
          // Attach the cart snapshot stashed by checkoutCallCenter.vue before the Klarna
          // redirect. Single-use and matched to THIS payment intent — every other checkout that
          // shares this page writes no snapshot, so they keep sending { paymentIntentId } only.
          let klarnaCartItems = [];
          try {
            const snap = JSON.parse(sessionStorage.getItem('pending_klarna_cart') || 'null');
            if (snap && snap.payment_intent_id === paymentIntentId && Array.isArray(snap.items)) {
              klarnaCartItems = snap.items;
            }
          } catch (e) {
            klarnaCartItems = [];
          }
          sessionStorage.removeItem('pending_klarna_cart');

          // Confirm on backend and fulfill order
          const confirmBody = { paymentIntentId: paymentIntentId };
          if (klarnaCartItems.length > 0) confirmBody.cartItems = klarnaCartItems;
          const response = await this.$axios.post(`${this.baseUrl}/api/confirm-klarna-payment`, confirmBody);

          if (response.data.status === 'success') {
            await this.$store.dispatch('setPrescriptionPreauths', response.data.prescriptionPreauths);

            // The checkout fired no `purchase` — it handed off to Klarna mid-payment
            // and never ran again. This is the first moment the SERVER has confirmed
            // the sale, which is the only moment the event is allowed to fire. The
            // ids come from this response; everything else was stashed pre-redirect.
            firePendingKlarnaPurchase(paymentIntentId, {
              transactionId: this.resolveTransactionId(response.data),
              orderId: response.data.order_id || response.data.orderId,
            });

            // Card checkout reports the call-center conversion inline; Klarna redirects away,
            // so replay the stashed conversion here before leaving the page.
            await this.postCallConversion(paymentIntentId);

            // Offer priority review on top of the confirmed order, then leave.
            // The context was stashed by whichever checkout started this payment;
            // with no stash, or no add-on, this hands straight through to the push.
            const offerContext = readFastTrackContext(paymentIntentId);
            if (offerContext) {
              this.offerFastTrackThen(offerContext, () => this.$router.push('/verify-address'));
            } else {
              this.$router.push('/verify-address');
            }
          } else {
            this.error = true;
            this.errorMessage = response.data.message || 'Order fulfillment failed. Please contact support.';
          }
        } else if (paymentIntent.status === 'processing') {
          // Klarna settles asynchronously, so `processing` is a normal intermediate state,
          // NOT a failure — the customer has approved and Stripe will move the intent to
          // succeeded on its own. Do not post /api/confirm-klarna-payment here: the payment
          // has not succeeded yet. The backend's payment_intent.succeeded webhook creates
          // the order when it settles, so there is nothing for this page to do but say so.
          // Before this branch existed, `processing` fell through to the generic else below
          // and told a paying customer to contact support.
          this.pending = true;
        } else if (paymentIntent.status === 'requires_payment_method') {
          this.error = true;
          this.errorMessage = 'Payment was not approved. Please try again with a different payment method.';
        } else {
          this.error = true;
          this.errorMessage = `Payment status: ${paymentIntent.status}. Please contact support if you were charged.`;
        }
      } catch (error) {
        console.error('Klarna confirmation error:', error);
        this.error = true;
        this.errorMessage = error.response?.data?.message || 'Failed to confirm payment. Please contact support.';
      } finally {
        this.loading = false;
      }
    },
    // Same order of preference every card checkout uses to name the transaction, so a
    // Klarna sale and a card sale of the same order carry the same kind of id. Returns
    // undefined rather than a made-up id — an invented transaction_id is worse than a
    // missing one, because it looks real to the ad platforms.
    resolveTransactionId(data) {
      if (!data) return undefined;
      if (data.transaction_id) return data.transaction_id;
      if (Array.isArray(data.prescriptionPreauths) && data.prescriptionPreauths.length > 0) {
        return data.prescriptionPreauths[0].transactionId;
      }
      return undefined;
    },
    // Replays the call-center conversion that checkoutCallCenter.vue stashed before the
    // Klarna redirect. No-op for every other checkout that shares this return page
    // (only the call-center flow writes `pending_call_conversion`). Mirrors the card
    // flow's sendConversionTracking: posts /api/call-conversion with 3 retries.
    async postCallConversion(paymentIntentId, attempt = 1) {
      let pending;
      try {
        pending = JSON.parse(sessionStorage.getItem('pending_call_conversion') || 'null');
      } catch (e) {
        pending = null;
      }
      if (!pending || !pending.c_id) return;

      // Guard against a stale/abandoned attempt posting the wrong sale: the stashed
      // payment intent must match the one we just confirmed.
      if (pending.payment_intent_id !== paymentIntentId) {
        sessionStorage.removeItem('pending_call_conversion');
        return;
      }

      try {
        const res = await this.$axios.post(`${this.baseUrl}/api/call-conversion`, {
          user_id: pending.user_id,
          user_name: pending.user_name,
          product_name: pending.product_name,
          amount: pending.amount,
          c_id: pending.c_id,
          payment_terms: pending.payment_terms,
          // Rep attribution, stashed by checkoutCallCenter.vue before the Klarna redirect.
          // `|| null` because a stash written by an older build won't carry these keys.
          sa_id: pending.sa_id || null,
          team_id: pending.team_id || null,
          v_agent: pending.v_agent || null,
        }, { timeout: 10000 });

        if (res.data && res.data.status === 'success') {
          sessionStorage.removeItem('pending_call_conversion');
          sessionStorage.removeItem('affiliate_conversion_id');
        } else if (attempt < 3) {
          setTimeout(() => this.postCallConversion(paymentIntentId, attempt + 1), 2000);
        }
      } catch (error) {
        console.error(`Call conversion post error (attempt ${attempt}):`, error);
        if (attempt < 3) {
          setTimeout(() => this.postCallConversion(paymentIntentId, attempt + 1), Math.pow(2, attempt) * 1000);
        }
      }
    },
    goBackToCheckout() {
      // Go back to whichever checkout page initiated the Klarna payment
      // The klarna-return route is always one level deeper than the checkout route
      const currentPath = this.$route.path;
      const checkoutPath = currentPath.replace(/\/klarna-return$/, '');
      // The checkout rebuilds a free offer line from its link, so the link's
      // offer params come back with the shopper (the checkout put them on return_url).
      this.$router.push({ path: checkoutPath, query: offerQuery(this.$route.query) });
    },
  },
};
</script>

<style scoped>
.klarna-return {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.klarna-return-content {
  text-align: center;
  padding: 40px;
  max-width: 480px;
}

.klarna-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.klarna-status-text {
  font-size: 18px;
  color: #374151;
  font-weight: 500;
}

.klarna-pending,
.klarna-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.klarna-pending h2 {
  font-size: 22px;
  color: #1f2937;
  margin: 0;
}

.klarna-pending p {
  font-size: 16px;
  color: #6b7280;
  margin: 0;
}

.klarna-error h2 {
  font-size: 22px;
  color: #1f2937;
  margin: 0;
}

.klarna-error p {
  font-size: 16px;
  color: #6b7280;
  margin: 0;
}

.klarna-retry-btn {
  margin-top: 8px;
  padding: 14px 32px;
  background: #0b3b3c;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
}

.klarna-retry-btn:hover {
  opacity: 0.9;
}
</style>
